// Grouped nav config for the docked side menu (design 5a). Routes and submenu
// item lists are reused verbatim from the flat `menuList` in
// components/menu/utils.tsx — only the grouping/presentation differs. Icons use
// the Ortex Kit glyphs (`fak fa-*`) where they exist, FA Pro Light (`fal`)
// otherwise, matching the existing side menu.

export interface SideMenuSubLink {
    text: string;
    link: string;
    icon: string;
    // Shown only to users entitled to the Options Platform (the
    // PRODUCT_OPTIONS_PLATFORM addon or the new-options-UI rollout group).
    // Filtered out for everyone else in MenuContent.
    requiresOptionsPlatform?: boolean;
}

export interface SideMenuItem {
    text: string;
    icon: string;
    link?: string;
    tag?: string;
    submenu?: SideMenuSubLink[];
    // Gated on the backend access probe (useTradingIdeasAccess), not on
    // userState entitlements — hidden until the probe resolves true.
    requiresTradingIdeasAccess?: boolean;
    // Opens the ORTEX AI chat modal instead of navigating: there is no /ai
    // route, so MenuContent renders these as a <button>, not a link.
    action?: "chat";
    // Off-site host: rendered as a plain anchor opening in a new tab, since
    // next/link can't route it and the reader is leaving the app.
    external?: boolean;
    // Shown to everyone; users without the Alt Data entitlement (Advanced and
    // above) get the upgrade dialog on click instead of the page.
    requiresAltData?: boolean;
}

export interface SideMenuGroup {
    label: string;
    items: SideMenuItem[];
}

const COMING_SOON = "/coming-soon";

// POPULAR chips (pinned top). Four, so the section stays a shortlist rather
// than a second catalog: everything here is also reachable from the groups
// below, Tools first.
export const popularChips: SideMenuItem[] = [
    {
        text: "Short Interest",
        icon: "fal fa-arrow-trend-down",
        link: "/short-interest",
        submenu: [
            { text: "Overview", link: "/short-interest?tab=overview", icon: "fal fa-chart-line-down" },
            { text: "Live Short Screener", link: "/short-interest?tab=live_short_screener", icon: "fal fa-chart-line-down" },
            { text: "Short Extremes", link: "/short-interest?tab=short_extremes", icon: "fal fa-chart-line-down" },
            { text: "Short Changes", link: "/short-interest?tab=short_changes", icon: "fal fa-chart-line-down" },
            { text: "Short HeatMap", link: "/short-interest?tab=heatmap", icon: "fal fa-chart-line-down" },
            { text: "Sector Averages", link: "/short-interest?tab=sector_averages", icon: "fal fa-chart-line-down" },
            { text: "Index Averages", link: "/short-interest?tab=index_averages", icon: "fal fa-chart-line-down" },
            { text: "European Short Flags", link: "/short-interest?tab=european_short_reports", icon: "fal fa-chart-line-down" },
            { text: "European Short Holders", link: "/short-interest?tab=european_short_holders", icon: "fal fa-chart-line-down" },
        ],
    },
    {
        text: "Options",
        icon: "fak fa-stock-options",
        link: "/options-platform",
        submenu: [
            // Options Platform addon — premium analytics. New routes under
            // /options-platform/* are gated by AddonGate. requiresOptionsPlatform
            // items are shown only to entitled users; non-members get the legacy
            // entries below only (filtered in MenuContent).
            { text: "Flow", link: "/options-platform/flow", icon: "fak fa-stock-options", requiresOptionsPlatform: true },
            { text: "Analyze", link: "/options-tools/analyze", icon: "fak fa-stock-options", requiresOptionsPlatform: true },
            // Legacy free chain views — kept for users without the addon.
            { text: "Interesting Options Flow", link: "/options?tab=interesting-options-flow", icon: "fak fa-stock-options" },
            { text: "Options Flow Sentiment", link: "/options?tab=option-flow-sentiment", icon: "fak fa-stock-options" },
            { text: "Options Extremes", link: COMING_SOON, icon: "fak fa-stock-options" },
        ],
    },
    { text: "Market Intelligence", icon: "fal fa-brain", link: "/market-intelligence" },
    { text: "Trading Ideas", icon: "fal fa-lightbulb", link: "/trading-ideas" },
    { text: "APIs", icon: "fal fa-code", link: "/apis/plans" },
];

export const sideMenuGroups: SideMenuGroup[] = [
    {
        label: "Tools",
        items: [
            { text: "Charts", icon: "fak fa-stock-trading-signals", link: "/charts" },
            { text: "Screener", icon: "fak fa-screening", link: "/screening" },
            { text: "ORTEX APIs", icon: "fal fa-code", link: "/apis/plans" },
            {
                text: "Heat Maps",
                icon: "fak fa-stock-trading-signals",
                submenu: [
                    { text: "Market HeatMap", link: "/heatmap", icon: "fak fa-stock-trading-signals" },
                    { text: "Short HeatMap", link: "/short-interest?tab=heatmap", icon: "fal fa-chart-line-down" },
                    { text: "Earnings HeatMap", link: "/earnings/heatmap", icon: "fak fa-stock-trading-signals" },
                ],
            },
            { text: "Backtester", icon: "fal fa-chart-line", link: "/backtester" },
            { text: "Alerts", icon: "fal fa-bell", link: "/alerts" },
            { text: "Custom Views", icon: "fal fa-table-columns", link: "/custom-views" },
            { text: "ORTEX AI", icon: "fal fa-wand-magic-sparkles", action: "chat" },
        ],
    },
    {
        label: "Markets",
        items: [
            { text: "Markets Overview", icon: "fak fa-markets-overview", link: "/markets-overview" },
            // Live movers are a market read, not a stock data set (Peter, 2026-09-27).
            { text: "Stock Movers", icon: "fak fa-charts", link: "/stock-movers" },
            { text: "Currencies", icon: "fal fa-dollar-sign", link: "/currencies" },
            { text: "Commodities", icon: "fal fa-oil-can", link: "/commodities" },
            { text: "Bond Yields", icon: "fal fa-percent", link: "/yields" },
            // Corporate and sovereign bonds: terms, ratings, maturity walls, lending (same tier as Alt Data).
            { text: "Bonds", icon: "fal fa-file-certificate", link: "/bonds", requiresAltData: true },
            { text: "Market Intelligence", icon: "fal fa-brain", link: "/market-intelligence" },
            { text: "Market Sentiment", icon: "fak fa-data-sentiment", link: "/market-sentiment" },
            { text: "Money Flow", icon: "fak fa-passive-money-flow", link: "/money-flow" },
            { text: "Index Rebalance", icon: "fal fa-balance-scale", link: "/index-rebalance" },
            { text: "Macro Events", icon: "fal fa-globe-europe", link: "/macro" },
            // Unemployment, inflation and claims: the macro datasets that sit with Alt Data (same tier).
            { text: "Macro Data", icon: "fal fa-globe", link: "/macro-data", requiresAltData: true },
            { text: "News", icon: "fal fa-newspaper", link: "/news" },
        ],
    },
    {
        // Every data set is a row: the group label is the only affordance a
        // reader needs, so nothing here hides behind a flyout.
        label: "Stock Data",
        items: [
            { text: "Alt Data", icon: "fal fa-satellite-dish", link: "/alt-data", requiresAltData: true },
            { text: "Stock Stamps", icon: "fak fa-stock-stamps", link: "/stock-stamps" },
            { text: "Short Interest", icon: "fal fa-chart-line-down", link: "/short-interest" },
            { text: "Options", icon: "fak fa-stock-options", link: "/options" },
            { text: "Insiders", icon: "fal fa-users", link: "/insiders" },
            { text: "Analysts", icon: "fal fa-user-tie", link: "/analysts" },
            { text: "News", icon: "fal fa-newspaper", link: "/news" },
            { text: "Company Events", icon: "fal fa-calendar-alt", link: "/events" },
            // Who reports next, with reaction history, implied move, shorts and the alt-data lead.
            { text: "Earnings", icon: "fal fa-calendar-check", link: "/earnings" },
            { text: "Earnings HeatMap", icon: "fak fa-stock-trading-signals", link: "/earnings/heatmap" },
            { text: "Holdings", icon: "fal fa-list-ol", link: "/holdings" },
            { text: "Buybacks", icon: "fal fa-arrows-to-circle", link: "/buybacks" },
            { text: "Dividends", icon: "fal fa-chart-pie-alt", link: "/dividends" },
            { text: "Stock Splits", icon: "fal fa-split", link: "/stock-splits" },
            { text: "IPOs & Deals", icon: "fak fa-ipos-deals", link: "/ipo-deals" },
            { text: "Lock-ups", icon: "fak fa-lock-solid-2", link: "/lock-ups" },
            { text: "Index Rebalance", icon: "fal fa-balance-scale", link: "/index-rebalance" },
            { text: "US Government Trades", icon: "fak fa-building-columns-regular", link: "/us-gov-trades" },
            { text: "Activist Filings", icon: "fal fa-bullhorn", link: "/activist-filings" },
            {
                text: "ETFs",
                icon: "fak fa-etfs",
                submenu: [
                    { text: "ETF Overview", link: "/etfs", icon: "fak fa-etfs" },
                    { text: "ETF Fund Flow", link: "/money-flow", icon: "fak fa-etfs" },
                    { text: "ETF Shorts", link: "/etf-shorts", icon: "fak fa-etfs" },
                ],
            },
        ],
    },
    {
        label: "Signals & Scores",
        items: [
            { text: "Trading Ideas", icon: "fal fa-lightbulb", link: "/trading-ideas", requiresTradingIdeasAccess: true },
            { text: "Trading Signals", icon: "fak fa-trading-signals", link: "/trading-signals" },
            { text: "Stock Scores", icon: "fak fa-stock-scores", link: "/stock-scores" },
            { text: "Sector Scores", icon: "fak fa-ranking-star", link: "/sector-scores" },
        ],
    },
];

// Pinned to the bottom of the panel.
export const myOrtexGroup: SideMenuGroup = {
    label: "My Ortex",
    items: [
        { text: "My Portfolio", icon: "fak fa-portfolio", link: "/portfolio" },
        { text: "My Stocks", icon: "fak fa-my-universe", link: "/my-universe" },
    ],
};
